import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearch } from "@tanstack/react-router";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { formatMemoDate } from "@/lib/format";
import { t } from "@/lib/strings";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useMemoDetail, useMemoMutations } from "@/hooks/useMemo";
import { useSeries } from "@/hooks/useMeetingSeries";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { workspaceColor } from "@/lib/colors";
import type { MemoUser, WorkspaceMember } from "@/lib/types";
import { MemoFindBar } from "@/components/memo/MemoFindBar";
import { MemoFindHighlights } from "@/components/memo/MemoFindHighlights";
import { MemoHeader } from "@/components/memo/MemoHeader";
import { memoPeopleById } from "@/lib/memoPeople";
import { MemoBlockView, type PendingInsert } from "@/components/memo/MemoBlockView";
import { MemoNewOnlyView } from "@/components/memo/MemoNewOnlyView";
import { countNewEntries } from "@/lib/memoNewEntries";
import { KuerzelDirectoryContext } from "@/components/memo/MemoMarkdown";
import { useKuerzelDirectory } from "@/hooks/useKuerzelDirectory";
import { BackToTop } from "@/components/ui/BackToTop";
import { MemoPresentBar } from "@/components/memo/MemoPresentBar";
import { buildFocusUnits, insertAnchorFor, pickNearestUnit, unitDomId } from "@/lib/memoFocusUnits";
import {
  INTERACTIVE_SELECTOR,
  controlToBlurOnPresentClick,
  insertIntentForKey,
} from "@/lib/memoPresentKeys";
import { unitScrollDelta } from "@/lib/memoPresentScroll";
import { resolveInsertTarget } from "@/lib/memoPresentInsert";
import { SectionNavRail } from "@/components/memo/SectionNavRail";
import { Icon } from "@/components/ui/Icon";
import { ghostButtonStyle } from "@/components/memo/ui/ProtokolleHeader";
import { usePresentationView } from "@/hooks/usePresentationView";
import { canEditHistory } from "@/lib/memoEditLock";
import { byPositionThenId } from "@/lib/positionOrder";
import { memoMoveSections } from "@/lib/memoMoveTargets";
import { useMemoFind } from "@/hooks/useMemoFind";

const sectionAnchorId = (blockId: string) => `memo-block-${blockId}`;

export function MemoPage() {
  const { memoId } = useParams({ strict: false }) as { memoId: string };
  const {
    entry: searchEntryId,
    block: searchBlockId,
    q: searchQ,
  } = useSearch({ strict: false }) as {
    entry?: string;
    block?: string;
    q?: string;
  };
  const handledAnchorRef = useRef<string | null>(null);
  const findContainerRef = useRef<HTMLDivElement>(null);
  const { data: memo, isLoading } = useMemoDetail(memoId);
  const { data: series } = useSeries(memo?.series_id);
  const { data: workspaceMembers = [] } = useWorkspaceMembers(series?.home_workspace_id);
  // Org-wide Kürzel directory, resolved once here (this page has the QueryClient)
  // and provided to every memo entry for the reference highlight.
  const kuerzelDir = useKuerzelDirectory();
  // The @-mention/assignee pool must include the SERIES participants + editors,
  // not just the home-workspace members — a Besprechungsserie spans workspaces,
  // so to-dos should be assignable to any participant (else, if you're the only
  // member of the workspace, you can only assign to yourself).
  const members = useMemo<WorkspaceMember[]>(() => {
    const byId = new Map<string, WorkspaceMember>();
    for (const m of workspaceMembers) byId.set(m.user_id, m);
    const add = (u: MemoUser) => {
      if (byId.has(u.id)) return;
      byId.set(u.id, {
        user_id: u.id,
        role: "member",
        joined_at: "",
        email: "",
        display_name: u.display_name,
        username: u.username,
        avatar_url: u.avatar_url,
        department_workspace_id: u.department_workspace_id ?? null,
        department_color: u.department_color ?? null,
      });
    };
    (memo?.participants ?? []).forEach(add);
    (series?.participants ?? []).forEach(add);
    (series?.editors ?? []).forEach(add);
    return [...byId.values()];
  }, [workspaceMembers, memo?.participants, series?.participants, series?.editors]);
  // 🔴 The faces the document draws come from the SAME union as the mention picker above —
  // never from `memo.participants`. Attendance is not assignment: a to-do can belong to
  // someone who was on holiday that week, and resolving them against the roster drew them as
  // no avatar at all. See lib/memoPeople.
  const peopleById = useMemo(() => memoPeopleById(members), [members]);
  const mut = useMemoMutations(memoId);
  const isDesktop = useBreakpoint("md");
  // The fullscreen presentation overlay is its own scroll container — needed so
  // focusing a point can position it precisely (center / upper-third) rather
  // than the minimal "nearest" nudge that could leave a point at the bottom.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoRenameId, setAutoRenameId] = useState<string | null>(null);
  const blocks = useMemo(() => (memo ? [...memo.blocks].sort(byPositionThenId) : []), [memo]);
  // Every Abschnitt with its open Zwischenabschnitte — the per-entry "Verschieben nach" menu.
  const moveSections = useMemo(() => memoMoveSections(blocks), [blocks]);
  // Deep-link from search (?entry= / ?block=): once blocks are loaded, scroll to
  // + briefly flash the matched point/section. Keyed on the anchor VALUE (not a
  // once-per-mount flag) so a second search into the SAME memo (different point)
  // still fires; the 5s poll keeps the same `blocks` ref so it never re-triggers
  // for an already-handled anchor. The param is left in the URL on purpose — no
  // navigate() here, which previously cleared the flash timeout on its own re-run.
  useEffect(() => {
    if (!blocks.length) return;
    const anchorId = searchEntryId
      ? `memo-entry-${searchEntryId}`
      : searchBlockId
        ? `memo-block-${searchBlockId}`
        : null;
    if (!anchorId || handledAnchorRef.current === anchorId) return;
    const el = document.getElementById(anchorId);
    if (!el) return; // not rendered yet — retry on the next blocks change
    handledAnchorRef.current = anchorId;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("flow-search-flash");
    const tid = window.setTimeout(() => el.classList.remove("flow-search-flash"), 1800);
    return () => window.clearTimeout(tid);
  }, [blocks, searchEntryId, searchBlockId]);
  // Presentation navigates FOCUS UNITS: a Zwischenabschnitt, or a whole Abschnitt
  // when a block has no sub-headings. One cursor level over a flat, document-
  // ordered list.
  const units = useMemo(() => buildFocusUnits(blocks), [blocks]);
  // Focus travels by ANCHOR, not index: a co-editor's insert/delete arriving via
  // the 5s poll must never slide the spotlight onto a different Zwischenabschnitt.
  const anchors = useMemo(() => units.map((u) => u.anchorId), [units]);
  const pres = usePresentationView(anchors);
  // "Nur Neues" — a transient, per-visit read filter (not persisted). `newCount`
  // drives the header toggle's visibility (it appears only when something is new).
  const [newOnly, setNewOnly] = useState(false);
  const newCount = useMemo(() => countNewEntries(blocks), [blocks]);
  // 🔴 The toggle only renders while `newCount > 0`, so if the last new point
  // disappears (a co-editor closes/deletes it via the 5s poll) while the filter
  // is ON, the button would vanish and strand the reader on the empty view with
  // no way back. Drop the filter when nothing is new so the full Protokoll returns.
  useEffect(() => {
    if (newOnly && newCount === 0) setNewOnly(false);
  }, [newOnly, newCount]);
  const {
    fullscreen: presenting,
    handleKey: presHandleKey,
    mode,
    focusedUnit,
    focusedAnchor,
    unlocked,
    unlockedAnchor,
    toggleUnlock,
    focusUnitAt,
  } = pres;

  // 🔴 Computed HERE, above the effects, not after the `if (!memo) return` guard
  // below: the presentation keydown effect gates the Enter/A insert on it, and a
  // hook cannot read a value declared after an early return. Same meaning — a
  // finalized memo needs the narrower right — just hoisted.
  const canEdit = memo
    ? memo.status === "finalized"
      ? memo.access.can_edit_finalized
      : memo.access.can_edit_draft
    : false;

  // In-memo find (Ctrl+F, or arrive via a Protokolle search hit → highlight the
  // term everywhere + step through matches). Normal-view only (disabled while
  // presenting); `blocks` is the content signal so ranges recompute after a poll.
  const find = useMemoFind({
    containerRef: findContainerRef,
    contentVersion: blocks,
    initialTerm: searchQ,
    initialAnchorPresent: !!(searchEntryId || searchBlockId),
    enabled: !presenting,
  });

  // The unit the reader is currently on. Geometry is measured here; the choice
  // itself lives in the pure `pickNearestUnit` so it can be tested.
  const nearestUnit = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return 0;
    const cRect = container.getBoundingClientRect();
    const extents = units.map((u) => {
      const el = document.getElementById(unitDomId(u));
      // Not mounted (collapsed / not yet rendered): never eligible, so it can't
      // win the scan just by being first.
      if (!el) return { top: Infinity, bottom: -Infinity };
      const r = el.getBoundingClientRect();
      return { top: r.top, bottom: r.bottom };
    });
    // Scroll-spy: the unit crossing the reading line (the upper third). See
    // pickNearestUnit — four hand-rolled rules failed here before it.
    // scrollTop CLAMPS the reading line: at rest it sits at the container's top edge,
    // so the first Zwischenabschnitt always wins regardless of viewport height or how
    // tall the memo header is. See pickNearestUnit.
    return pickNearestUnit(extents, cRect.top, cRect.bottom, container.scrollTop);
  }, [units]);

  // Two different scroll intents share one focus state. Cursor navigation ARRIVES
  // at a unit from outside, so positioning the whole unit is right. But focusing
  // an ENTRY (a point you just added, or clicked to edit) must not re-position the
  // unit: inside a Zwischenabschnitt taller than the screen that yanks you back to
  // its beginning. This ref marks the entry-driven case.
  const skipUnitScrollRef = useRef(false);

  // 🔴 Read `units` through a ref inside the unit-centring effect below. It is only
  // needed to look the element up — it is NOT a scroll trigger. Listing it in the
  // deps re-ran the effect on every refetch (saving an entry invalidates the memo
  // query, which rebuilds `blocks` and mints a new `units` array), re-centring the
  // Zwischenabschnitt and yanking the writer back up to its heading even though
  // focus had not moved. Reported by the minute-taker: "wenn ich was speichere,
  // springt er wieder ins Thema rauf".
  const unitsRef = useRef(units);
  unitsRef.current = units;

  // Free-mode "you are here": tracked so the breadcrumb stays oriented while the
  // reader scrolls with the mouse.
  const [nearest, setNearest] = useState(0);
  const { release } = pres;
  useEffect(() => {
    if (!presenting) return;
    const container = scrollRef.current;
    if (!container) return;
    let raf: number | null = null;
    const onUserScroll = () => {
      release();
      // nearestUnit() measures every unit's rect; coalesce to one measurement per
      // frame or a fast trackpad flick forces hundreds of reflows per second.
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        setNearest(nearestUnit());
      });
    };
    // wheel/touchmove ONLY — a `scroll` listener would also fire for our own
    // programmatic scrollTo when locking, instantly self-unlocking.
    container.addEventListener("wheel", onUserScroll, { passive: true });
    container.addEventListener("touchmove", onUserScroll, { passive: true });
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      container.removeEventListener("wheel", onUserScroll);
      container.removeEventListener("touchmove", onUserScroll);
    };
  }, [presenting, release, nearestUnit]);

  // While presenting, drive focus with the keyboard (arrows step units, Esc
  // unlocks then exits, Enter/A insert) — but never hijack keys while typing.
  // NOTE: there is no `F` binding, despite what this comment used to claim —
  // `toggleFullscreen` is exported by the hook and called by nothing. So Enter/A
  // are the FIRST plain-key bindings here; nothing established that they are safe.
  useEffect(() => {
    if (!presenting) return;
    const onKey = (e: KeyboardEvent) => {
      // A dialog owns the keyboard while open (its Escape closes it, not the
      // presentation) — don't drive presentation keys underneath it.
      if (document.querySelector('[role="dialog"]')) return;
      const el = e.target as HTMLElement | null;
      const typing = !!el?.isContentEditable || ["INPUT", "TEXTAREA"].includes(el?.tagName ?? "");
      // While typing (incl. the @-mention popup), the editor owns every key,
      // Escape included — so editing a note never advances/exits presentation.
      if (typing) return;

      // Enter = neuer Eintrag, A = neue Aufgabe, into the FOCUSED
      // Zwischenabschnitt. This completes the minute-taker's keyboard loop whose
      // other half 0a5a59c shipped: ↓ → Enter → type → Ctrl+Enter → ↓ → …
      //
      // The guards (interactive focus / modifiers / key-repeat) live in the pure
      // `insertIntentForKey` — each of them exists because it was a real bug, and
      // inline in this effect nothing could reach them with a test.
      const intent = insertIntentForKey({
        key: e.key,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        altKey: e.altKey,
        repeat: e.repeat,
        targetIsEditable: typing,
        targetIsInteractive: !!el?.closest(INTERACTIVE_SELECTOR),
      });
      if (intent && canEdit) {
        // 🔴 In `free` mode this GRABS the unit at the reading line rather than standing
        // down. Requiring a locked unit made Enter a SILENT no-op right after entering
        // presentation and after every scroll (both leave you in `free`, and Enter cannot
        // lock — handleKey returns false for it). It read as "Enter works on the third or
        // fourth press", because an arrow key had to happen first.
        const target = resolveInsertTarget({
          mode,
          focusedUnit,
          nearestUnit: nearestUnit(),
          unitCount: units.length,
        });
        const unit = target ? units[target.index] : undefined;
        if (target && unit) {
          e.preventDefault();
          if (target.needsLock) {
            // A grab never re-positions the viewport — the reader is already looking at it.
            skipUnitScrollRef.current = true;
            focusUnitAt(target.index);
          }
          insertNonceRef.current += 1;
          setPendingInsert({
            blockId: unit.blockId,
            afterEntryId: insertAnchorFor(unit),
            asTask: intent === "task",
            nonce: insertNonceRef.current,
          });
          return;
        }
      }

      // 🔴 A STEP (locked→locked) arrives at a unit from outside, so it positions
      // it. A GRAB (free→locked) does NOT: it locks the unit the reader is already
      // inside, at the scroll position they chose. Re-positioning it drags the
      // shared Teams screen backwards — up to 2400px, mid-sentence, in front of the
      // room. That was the reported "es springt zu weit hoch", and it outlived four
      // attempts at fixing the PICK because the pick was never what moved the screen.
      // 🔴 Same predicate the hook uses to decide grab-vs-step
      // (usePresentationView.handleKey). They must not diverge: for one render after a
      // co-editor's delete orphans the anchor, `mode` is still "locked" while
      // `focusedUnit` is already null — the hook would GRAB while this armed a STEP,
      // and the step positions.
      skipUnitScrollRef.current = mode === "free" || focusedUnit === null;
      if (presHandleKey(e.key, nearestUnit())) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [presenting, presHandleKey, nearestUnit, canEdit, mode, focusedUnit, units, focusUnitAt]);

  // The anchor of the currently focused unit (null while free) — drives the dim.
  const focusedAnchorId = mode === "locked" ? focusedAnchor : null;
  // Never dim with nothing focused: for one frame after a unit disappears the
  // anchor is null while mode is still "locked", which would flash the WHOLE memo
  // dimmed and non-interactive before the release effect runs.
  const dimOthers = presenting && mode === "locked" && focusedAnchorId !== null;

  // Centre the focused unit in the scroll container. A unit that FITS is centred;
  // a unit TALLER than the viewport has its beginning placed in the upper third,
  // so a long Zwischenabschnitt reads downward from a natural start rather than
  // being centred on its middle. A dead-zone band leaves a unit that already sits
  // comfortably alone, so stepping between two units that share the screen
  // doesn't fire a smooth re-scroll on every press.
  useEffect(() => {
    if (!presenting || mode !== "locked" || focusedAnchor === null) return;
    if (skipUnitScrollRef.current) {
      // Entry-driven focus: the entry scroll below owns the viewport.
      skipUnitScrollRef.current = false;
      return;
    }
    // Resolved by ANCHOR, not by index — the index is what a co-editor's poll shifts.
    const unit = unitsRef.current.find((u) => u.anchorId === focusedAnchor);
    const container = scrollRef.current;
    if (!unit || !container) return;
    const el = document.getElementById(unitDomId(unit));
    if (!el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const delta = unitScrollDelta({
      elTop: eRect.top - cRect.top,
      elHeight: eRect.height,
      viewportH: cRect.height,
      // 🔴 The step must park the unit so it OWNS the same reading line the grab
      // probes with — which is clamped by scrollTop, so the positioner needs it too.
      scrollTop: container.scrollTop,
    });
    if (delta === 0) return;
    container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
    // 🔴 Keyed on the ANCHOR, not the index. `focusedUnit` is
    // `anchors.indexOf(focusedAnchor)`, so a co-editor's insert into an EARLIER block
    // (arriving on the 5s poll) shifts the index without the focus having moved. That
    // re-ran this effect with `skipUnitScrollRef` already consumed — and on a long
    // Zwischenabschnitt the reader was deep inside, it smooth-scrolled the shared
    // Teams screen back 2352px to its beginning, mid-sentence, with NO key pressed.
    // The anchor changes exactly when the focus really moves.
  }, [presenting, mode, focusedAnchor]);

  // When a point is added/clicked while presenting, lock the UNIT that contains
  // it (so it's spotlighted, not left behind) — resolved once the entry exists in
  // the data (a fresh insert only appears after the mutation round-trips).
  const [pendingFocusEntryId, setPendingFocusEntryId] = useState<string | null>(null);
  // A keyboard insert while presenting (Enter / A). The owning MemoBlockView
  // executes it; the nonce makes two consecutive Enters two distinct intents.
  const [pendingInsert, setPendingInsert] = useState<PendingInsert | null>(null);
  const insertNonceRef = useRef(0);
  useEffect(() => {
    if (!pendingFocusEntryId) return;
    // Clear a stale pending target when we're not presenting (e.g. exited before
    // the just-added entry's refetch landed) so it can't snap focus later.
    if (!presenting) {
      setPendingFocusEntryId(null);
      return;
    }
    const i = units.findIndex(
      (u) =>
        u.entryIds.includes(pendingFocusEntryId) || u.subheadingEntryId === pendingFocusEntryId,
    );
    if (i >= 0) {
      // Spotlight the unit for the dim + breadcrumb, but keep the viewport where
      // the user is working: `nearest` is a no-op when the entry is already on
      // screen, and only nudges when it is just past the fold.
      skipUnitScrollRef.current = true;
      focusUnitAt(i);
      const target = pendingFocusEntryId;
      requestAnimationFrame(() => {
        document
          .getElementById(`memo-entry-${target}`)
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
      setPendingFocusEntryId(null);
    }
  }, [pendingFocusEntryId, units, presenting, focusUnitAt]);

  // Jump to a to-do from the header's open-tasks panel.
  //
  // Reuses the EXACT scroll + flash the Protokolle search deep-link already does
  // (`flow-search-flash`), so arriving at a line looks the same however you got there — from
  // a search hit, or from the panel. Nothing new was needed for this; it is wiring.
  //
  // 🔴 While presenting it must also move the CURSOR, not just the viewport. The shared screen
  // dims everything but the focused Zwischenabschnitt, so scrolling to a to-do without
  // spotlighting its unit would leave the room staring at a dimmed line. `skipUnitScrollRef`
  // stops the focus effect from ALSO scrolling — the entry scroll below is the precise one and
  // should win; two competing smooth-scrolls fight and land somewhere between them.
  const jumpToEntry = useCallback(
    (entryId: string) => {
      if (presenting) {
        const i = units.findIndex(
          (u) => u.entryIds.includes(entryId) || u.subheadingEntryId === entryId,
        );
        if (i >= 0) {
          skipUnitScrollRef.current = true;
          focusUnitAt(i);
        }
      }
      // After the focus re-render, so the element is not mid-remount when we measure it.
      requestAnimationFrame(() => {
        const el = document.getElementById(`memo-entry-${entryId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("flow-search-flash");
        window.setTimeout(() => el.classList.remove("flow-search-flash"), 1800);
      });
    },
    [presenting, units, focusUnitAt],
  );

  // Add a section and immediately open its title for naming (fresh sections are
  // created with a default label; the new block opens straight into rename).
  const addSection = async () => {
    const updated = await mut.addBlock.mutateAsync(t.memoAddBlock.replace("+ ", ""));
    const last = [...updated.blocks].sort(byPositionThenId).pop();
    if (last) setAutoRenameId(last.id);
  };

  // Reorder a section one step. The SERVER places it, from the stored order — this used
  // to send a midpoint computed from the neighbours on screen, and because a move only
  // shows after its round trip, a second move into the same gap computed the same
  // midpoint: two Abschnitte on one number, swapping on every poll (2026-10-05).
  //
  // One move at a time: a click while a move is still in flight is IGNORED, so the next
  // click acts on the order the last one produced, not on a stale screen. Ignored, not
  // disabled — disabling dimmed every arrow on the page for each click and dropped
  // keyboard focus off the one you were using.
  const moveBlock = (index: number, direction: "up" | "down") => {
    if (mut.moveBlock.isPending) return;
    const block = blocks[index];
    if (block) mut.moveBlock.mutate({ blockId: block.id, direction });
  };

  if (isLoading || !memo) {
    return <div style={{ padding: "28px 36px", color: "var(--ink-3)" }}>…</div>;
  }

  // Rewriting an earlier meeting's record is a narrower right than editing the
  // draft: series editors, the home workspace's owner/admin, and flow-admins.
  const mayEditHistory = canEditHistory(memo.access);
  const accentColor = series ? workspaceColor(series.home_workspace_id) : "var(--ink-4)";
  // "Nur Neues" is mutually exclusive with presenting (the state hooks live up
  // top with the other hooks — see `newOnly`/`newCount`).
  const showNewOnly = newOnly && !pres.fullscreen;
  // The breadcrumb shows the locked unit, or (while free-scrolling) the unit the
  // reader is currently on.
  const barIndex = mode === "locked" ? focusedUnit : nearest;

  return (
    <div
      ref={scrollRef}
      // 🔴 While presenting, a clicked button must not KEEP DOM focus. Chrome focuses
      // it on mousedown, and the insert keys (Enter / A) deliberately stand down
      // while anything interactive is focused — so clicking the eye button to focus a
      // section, ticking a point off, or hitting "Bearbeiten entsperren" silently
      // killed the keyboard loop that is the whole point of the feature.
      //
      // 🔴 Blur AFTER the click, not preventDefault on mousedown. Suppressing the
      // mousedown default also suppresses focus LEAVING the open editor — and the
      // memo editors commit on blur (MemoEntryRow.exitEditor). Clicking a button
      // mid-bullet would then leave the entry uncommitted AND the editor stuck open,
      // with the caret still in it, so the arrow keys went back to moving the caret.
      // Letting mousedown do its normal job blurs the editor (it commits), and we
      // simply hand focus back to the document afterwards.
      //
      // ONE capture handler rather than a prop per button: an earlier fix patched two
      // buttons and missed three — the retire ✓, the reopen checkbox and the move
      // menu, which are exactly the ones a presenter clicks mid-meeting.
      onClickCapture={(e) => {
        if (!pres.fullscreen) return;
        // 🔴 POINTER clicks only. A keyboard activation (Enter/Space on a focused
        // button) also dispatches a `click` — with `detail === 0`. Blurring there
        // hands focus to <body>, so the NEXT Enter no longer sees an interactive
        // target and inserts an empty Eintrag onto the shared screen instead of
        // toggling the button again. That is the exact failure the insert guard
        // exists to prevent, reintroduced by its own fix.
        // The rule is pure and tested (lib/memoPresentKeys). It exists there, not inline here,
        // because the guard that matters most — NEVER blur a text editor, because TipTap's
        // ProseMirror div carries tabindex="0" and so looks like a button to INTERACTIVE_SELECTOR
        // — was invisible to every test while it lived in this handler, and shipped a bug that
        // closed the editor whenever a writer clicked to place her caret mid-presentation.
        controlToBlurOnPresentClick({
          target: e.target as HTMLElement | null,
          activeElement: document.activeElement,
          detail: e.detail,
        })?.blur();
      }}
      style={
        pres.fullscreen
          ? {
              position: "fixed",
              inset: 0,
              // Above the app chrome (Topbar dropdowns z=60) but BELOW the portal
              // layer (mention 200 / dialog 300 / popover 400 / toast 500) so
              // pickers, the @-mention popup and toasts stay visible while presenting.
              zIndex: 100,
              overflow: "auto",
              background: "var(--surface)",
              // Tighter horizontal padding in presentation so the bigger zoomed
              // column (below) has room to grow without overflowing on a laptop.
              // The 45vh BOTTOM is scroll-past-end room (as in the normal view): it
              // lets the last/newest entry lift off the bottom edge — writing a fresh
              // point live in a meeting no longer jams it against the screen bottom —
              // and gives the focus cursor (unitScrollDelta) the room to actually
              // CENTER the last unit, which it otherwise can't reach. Safe against the
              // presentation scroll math: pickNearestUnit already handles "scrolled
              // past every unit into the trailing whitespace" (stays on the last
              // mounted unit), and the padding carries no unit, so no reading-line math
              // changes.
              padding: isDesktop ? "40px max(24px, 4vw) 45vh" : "20px 16px 45vh",
            }
          : // Trailing "scroll-past-end" room (like an editor's scroll-beyond-last-line):
            // without it the last entry sits glued one line above the bottom edge. The
            // extra paddingBottom lets you keep scrolling until the newest entry rises to
            // a comfortable upper-middle height — matters most while writing during a
            // meeting. Normal view only; presentation has its own focus-centering scroll.
            { padding: "24px 36px 45vh", maxWidth: 1180, margin: "0 auto" }
      }
    >
      {canEdit ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8, gap: 10 }}>
          {pres.fullscreen ? (
            <span style={{ fontSize: 12, color: "var(--ink-4)", alignSelf: "center" }}>
              {t.memoPresentFocusHint}
            </span>
          ) : null}
          <button
            type="button"
            onClick={(e) => {
              // 🔴 Hand focus back to the document ON THIS BUTTON SPECIFICALLY.
              //
              // The container's onClickCapture blurs clicked controls while presenting —
              // but it bails on `!pres.fullscreen`, and during the click that ENTERS
              // presentation `fullscreen` is still false. This is also the one button
              // that does not unmount (it just becomes "Präsentation beenden"), so it
              // kept DOM focus for the whole presentation.
              //
              // Consequence, and the reported bug: arrow keys still worked (they are not
              // guarded on focus), so the section locked and everything LOOKED fine — but
              // Enter and A stand down while anything interactive is focused, so they
              // were silently dead. Clicking the eye button next to an entry happened to
              // fix it, because by then `fullscreen` was true and the container's handler
              // blurred that button. Hence "Enter works, but only if I start focus mode
              // by clicking rather than by pressing →".
              e.currentTarget.blur();
              if (pres.fullscreen) pres.exit();
              else pres.enter();
            }}
            style={ghostButtonStyle}
          >
            <Icon name={pres.fullscreen ? "x" : "expand"} size={15} />
            {pres.fullscreen ? t.memoPresentExit : t.memoPresent}
          </button>
        </div>
      ) : null}
      {pres.fullscreen ? null : (
        <Breadcrumb
          items={[
            { label: t.navHome, to: "/" },
            { label: t.meetingsTitle, to: "/meetings" },
            {
              label: series?.name ?? "…",
              to: "/meetings/$seriesId",
              params: { seriesId: memo.series_id },
            },
            { label: `${memo.title} · ${formatMemoDate(memo.meeting_date)}` },
          ]}
        />
      )}
      <MemoHeader
        memo={memo}
        series={series}
        mut={mut}
        onJumpToEntry={jumpToEntry}
        people={peopleById}
        newOnly={newOnly}
        onToggleNewOnly={() => setNewOnly((v) => !v)}
        newCount={pres.fullscreen ? 0 : newCount}
      />

      {memo.status === "finalized" && !memo.access.can_edit_finalized ? (
        <div
          style={{
            marginTop: 12,
            padding: "8px 12px",
            background: "rgba(0,89,155,0.06)",
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            color: "var(--ink-3)",
            fontSize: 12.5,
          }}
        >
          {t.memoReadOnlyHint}
        </div>
      ) : null}

      <div style={{ marginTop: 18, display: "flex", gap: 28, alignItems: "flex-start" }}>
        {isDesktop && !pres.fullscreen ? (
          <SectionNavRail
            sections={blocks.map((block) => ({
              id: sectionAnchorId(block.id),
              title: block.title,
              // Count points, not sub-headings, so the rail badge reads as the
              // number of actual entries.
              entryCount: block.entries.filter((e) => e.kind === "point").length,
            }))}
            canEdit={canEdit}
            onAddSection={() => void addSection()}
          />
        ) : null}

        <div
          ref={findContainerRef}
          style={{
            flex: 1,
            minWidth: 0,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 18,
            // Presentation: genuinely enlarge the whole protocol (readable when
            // screen-shared over Teams) via a real layout zoom — no per-section
            // transform, so a focused section can never physically overlap the
            // next one. Bigger zoom for Teams legibility; the column width is
            // trimmed in step (rendered width ≈ 800·1.34 ≈ 1072px) so the larger
            // text still fits a laptop without a horizontal scrollbar. Desktop-
            // only so it can't overflow a phone.
            ...(pres.fullscreen
              ? {
                  maxWidth: isDesktop ? 800 : 900,
                  margin: "0 auto",
                  width: "100%",
                  ...(isDesktop ? { zoom: 1.34 } : null),
                }
              : null),
          }}
        >
          {showNewOnly ? (
            <KuerzelDirectoryContext.Provider value={kuerzelDir}>
              <MemoNewOnlyView blocks={blocks} people={peopleById} accentColor={accentColor} />
            </KuerzelDirectoryContext.Provider>
          ) : null}
          {find.open && !pres.fullscreen && !showNewOnly ? (
            <>
              <MemoFindBar
                term={find.term}
                count={find.count}
                current={find.current}
                onTermChange={find.setTerm}
                onStep={find.step}
                onClose={find.close}
              />
              <MemoFindHighlights rects={find.rects} current={find.current} />
            </>
          ) : null}
          {!showNewOnly &&
            blocks.map((block, i) => (
              <div
                key={block.id}
                id={sectionAnchorId(block.id)}
                style={{ scrollMarginTop: pres.fullscreen ? 28 : 76 }}
              >
                <KuerzelDirectoryContext.Provider value={kuerzelDir}>
                  <MemoBlockView
                    block={block}
                    memoId={memo.id}
                    finalized={memo.status === "finalized"}
                    mut={mut}
                    canEdit={canEdit}
                    members={members}
                    autoRename={block.id === autoRenameId}
                    accentColor={accentColor}
                    presenting={pres.fullscreen}
                    focusedAnchorId={focusedAnchorId}
                    dimOthers={dimOthers}
                    mayEditHistory={mayEditHistory}
                    unlockedAnchorId={unlockedAnchor}
                    onFocus={() => {
                      // The section's focus button locks this block's FIRST unit.
                      const j = units.findIndex((u) => u.blockId === block.id);
                      // Arrives from outside the unit → position it (see skipUnitScrollRef).
                      skipUnitScrollRef.current = false;
                      if (j >= 0) pres.focusUnitAt(j);
                    }}
                    onFocusEntry={(entryId) => setPendingFocusEntryId(entryId)}
                    onMoveUp={i > 0 ? () => moveBlock(i, "up") : null}
                    onMoveDown={i < blocks.length - 1 ? () => moveBlock(i, "down") : null}
                    sections={moveSections}
                    isAdmin={memo.access.can_admin}
                    pendingInsert={pendingInsert}
                    onInsertHandled={() => setPendingInsert(null)}
                  />
                </KuerzelDirectoryContext.Provider>
              </div>
            ))}
        </div>
      </div>

      {/* Structure is frozen while presenting; this mobile add-section button sits
          inside the fullscreen overlay, so it needs the guard too (desktop is safe
          only because SectionNavRail hides when fullscreen). */}
      {canEdit && !isDesktop && !presenting ? (
        <button
          type="button"
          onClick={() => void addSection()}
          style={{
            marginTop: 16,
            background: "transparent",
            border: "1px dashed var(--hairline)",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 13,
            color: "var(--ink-3)",
            cursor: "pointer",
          }}
        >
          {t.memoAddBlock}
        </button>
      ) : null}

      {/* "Where am I" bar. z=101 sits above the presentation overlay (100) but
          below the portal layer (200-500), so dialogs / the @-mention popup and
          toasts stay visible while presenting. */}
      {presenting ? (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 101 }}>
          <MemoPresentBar
            units={units}
            index={barIndex}
            locked={mode === "locked"}
            mayEditHistory={mayEditHistory}
            unlocked={unlocked}
            onToggleUnlock={toggleUnlock}
          />
        </div>
      ) : null}

      {/* 🔴 The shell's "Nach oben" is bound to <main> and painted at z=45 — so while
          presenting it was both listening to the wrong element AND buried under this
          overlay (z=100). It never appeared in the one place a long Protokoll most needs
          it: scrolled deep, in front of a room. Bound to THIS scroll container, and lifted
          above the overlay like the present-bar above. Only while presenting — outside it
          the shell's own button is the right one, and two would fight for the same corner. */}
      {presenting ? <BackToTop containerRef={scrollRef} zIndex={101} /> : null}
    </div>
  );
}
